
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartEmpty from './components/CartEmpty';
import CartItemRow from './components/CartItemRow';
import CartSummary from './components/CartSummary';
import {
  getCart,
  updateCartItemQuantity,
  removeFromCart,
  clearCart,
  type CartItem,
} from '../../utils/cart';
import { useSEO } from '../../utils/seo';
import { useProducts } from '../../hooks/useProducts';

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const { products } = useProducts();
  const [stockError, setStockError] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useSEO({
    title: 'Panier | Daliah Secret',
    description: 'Votre panier d\u0027achats - Daliah Secret',
    canonical: '/cart',
    noindex: true,
  });

  useEffect(() => {
    setCartItems(getCart());
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => setCartItems(getCart());
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const handleQuantityChange = (id: number, newQuantity: number) => {
    const product = products.find((p) => parseInt(p.id) === id);
    const maxStock = product?.stock_quantity;

    if (newQuantity < 1) {
      removeFromCart(id);
      return;
    }

    if (maxStock !== undefined && newQuantity > maxStock) {
      setStockError(
        `Stock maximum atteint pour ce produit (${maxStock} unité${maxStock > 1 ? 's' : ''})`,
      );
      setTimeout(() => setStockError(''), 3000);
      return;
    }

    const result = updateCartItemQuantity(id, newQuantity, maxStock);
    if (!result.success && result.message) {
      setStockError(result.message);
      setTimeout(() => setStockError(''), 3000);
    }
  };

  const handleRemove = (id: number) => {
    removeFromCart(id);
  };

  const handleClearCart = () => {
    clearCart();
    setShowClearConfirm(false);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <CartEmpty />
        <Footer />
      </div>
    );
  }

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Stock Error Toast */}
      {stockError && (
        <div className="fixed top-24 right-4 z-50 px-5 py-3.5 rounded-xl shadow-lg bg-red-500 animate-fade-in">
          <div className="flex items-center gap-2.5 text-white">
            <i className="ri-error-warning-line text-lg w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium">{stockError}</span>
          </div>
        </div>
      )}

      {/* Clear Cart Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full shadow-2xl animate-fade-in">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-serif text-gray-900 text-center mb-2">
              Vider le panier ?
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Cette action supprimera tous les articles de votre panier. Elle est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={handleClearCart}
                className="flex-1 py-2.5 bg-red-500 text-white text-sm font-medium rounded-full hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Oui, vider
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-24 sm:pt-28 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6 sm:mb-8">
            <Link to="/" className="hover:text-[#D4A574] transition-colors cursor-pointer">Accueil</Link>
            <i className="ri-arrow-right-s-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <Link to="/products" className="hover:text-[#D4A574] transition-colors cursor-pointer">Boutique</Link>
            <i className="ri-arrow-right-s-line text-sm w-4 h-4 flex items-center justify-center"></i>
            <span className="text-gray-700 font-medium">Panier</span>
          </nav>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 sm:mb-10">
            <div>
              <h1 className="text-3xl sm:text-4xl font-serif text-gray-900 mb-1">
                Votre Panier
              </h1>
              <p className="text-sm text-gray-500">
                {totalItems} article{totalItems > 1 ? 's' : ''} sélectionné{totalItems > 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 text-sm text-[#D4A574] hover:text-[#C4956A] transition-colors cursor-pointer group self-start sm:self-auto"
            >
              <i className="ri-arrow-left-line text-base group-hover:-translate-x-1 transition-transform duration-300"></i>
              Continuer vos achats
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">

            {/* Cart Items */}
            <div className="lg:col-span-7 xl:col-span-8">
              {/* Column Headers (Desktop) */}
              <div className="hidden sm:flex items-center justify-between px-5 pb-3 mb-1 border-b border-gray-100">
                <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Produit</span>
                <div className="flex items-center gap-16">
                  <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Quantité</span>
                  <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Total</span>
                </div>
              </div>

              {/* Items List */}
              <div className="divide-y divide-gray-50">
                {cartItems.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    onQuantityChange={handleQuantityChange}
                    onRemove={handleRemove}
                  />
                ))}
              </div>

              {/* Bottom Actions */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 px-4 sm:px-5">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  Vider le panier
                </button>
                <button
                  onClick={() => navigate('/products')}
                  className="inline-flex items-center gap-1.5 text-xs text-[#D4A574] hover:text-[#C4956A] transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  Ajouter d&apos;autres articles
                </button>
              </div>

              {/* Reassurance Banner */}
              <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-3">
                <div className="flex flex-col items-center text-center p-3 sm:p-4 rounded-xl bg-[#F9F6F2]">
                  <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
                    <i className="ri-truck-line text-[#D4A574] text-base"></i>
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Livraison rapide</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5">2-5 jours ouvrés</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 sm:p-4 rounded-xl bg-[#F9F6F2]">
                  <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
                    <i className="ri-shield-check-line text-[#D4A574] text-base"></i>
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Paiement sécurisé</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5">SSL 256-bit</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 sm:p-4 rounded-xl bg-[#F9F6F2]">
                  <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
                    <i className="ri-arrow-go-back-line text-[#D4A574] text-base"></i>
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700">Retours gratuits</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-400 mt-0.5">Sous 30 jours</span>
                </div>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-5 xl:col-span-4">
              <CartSummary items={cartItems} />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
